import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CLP } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Copy, Loader2, Info, Tag, BarChart3, MapPin, Wallet, Users, Clock } from "lucide-react";
import { toast } from "sonner";

const FIELDS = ["name", "description", "category", "level", "duration_min", "price_clp", "capacity",
  "gift_enabled", "includes", "bring", "images", "address_id"];

const fmtDuration = (m) => {
  const h = Math.floor(m / 60), r = m % 60;
  return [h ? `${h} h` : "", r ? `${r} min` : ""].filter(Boolean).join(" ") || "—";
};

const Row = ({ icon: Icon, label, value, testId, nowrap }) => (
  <div className="flex items-start gap-2 sm:gap-3 py-2.5">
    <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-sm font-medium ${nowrap ? "whitespace-nowrap" : "break-words"}`} data-testid={testId}>{value || "—"}</p>
    </div>
  </div>
);

export function CopyWorkshopDialog({ workshop, onClose, onDone }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/business/addresses").then((r) => setAddresses(r.data)).catch(() => {}); }, []);

  const copyName = `${(workshop.name || "").trim()} COPIA`;
  const addr = addresses.find((a) => a.id === workshop.address_id) || addresses.find((a) => a.is_default) || addresses[0];
  const addrText = addr ? [addr.address, addr.commune, addr.region].filter(Boolean).join(", ") : "";

  const confirm = async () => {
    setLoading(true);
    try {
      const payload = Object.fromEntries(FIELDS.filter((k) => workshop[k] !== undefined).map((k) => [k, workshop[k]]));
      await api.post("/business/workshops", { ...payload, name: copyName, offer: { enabled: false } });
      toast.success("Copia creada. Ya puedes editarla y agregarle fechas de sesión.");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[92vh] overflow-y-auto" data-testid="copy-dialog">
        <DialogHeader>
          <div className="mx-auto sm:mx-0 mb-1 grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary">
            <Copy className="w-5 h-5" />
          </div>
          <DialogTitle className="text-base leading-snug" data-testid="copy-dialog-title">Copia de taller</DialogTitle>
          <p className="font-semibold text-primary break-words" data-testid="copy-dialog-name">{workshop.name}</p>
          <DialogDescription>Datos del taller original (solo informativo).</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border bg-muted/30 px-4 divide-y" data-testid="copy-dialog-info">
          <Row icon={Copy} label="Nombre del taller" value={workshop.name} testId="copy-info-name" />
          <div className="py-2.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Descripción del taller</p>
            <p className="text-sm whitespace-pre-line break-words line-clamp-6" data-testid="copy-info-desc">{workshop.description || "—"}</p>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <Row icon={Tag} label="Categoría" value={workshop.category} testId="copy-info-category" />
            <Row icon={BarChart3} label="Nivel" value={workshop.level || "Sin nivel"} testId="copy-info-level" />
          </div>
          <Row icon={MapPin} label="Dirección del taller" value={addrText} testId="copy-info-address" />
          <div className="grid grid-cols-[auto_auto_auto] justify-between gap-x-2">
            <Row icon={Wallet} label="Precio" value={CLP(workshop.price_clp)} testId="copy-info-price" nowrap />
            <Row icon={Users} label="Cupos" value={workshop.capacity} testId="copy-info-capacity" />
            <Row icon={Clock} label="Tiempo" value={fmtDuration(workshop.duration_min || 0)} testId="copy-info-duration" nowrap />
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm" data-testid="copy-dialog-note">
          <Info className="w-4 h-4 mt-0.5 text-primary shrink-0" />
          <span>Una vez hecha la copia del taller, podrás editarlo.</span>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} data-testid="copy-cancel">Cancelar</Button>
          <Button onClick={confirm} disabled={loading} data-testid="copy-confirm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Copy className="w-4 h-4 mr-1.5" />Copiar</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
